import { FC, useCallback, useEffect } from 'react';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import parse from 'html-react-parser';

import ConfirmationModal from '@components/ConfirmationModal/ConfirmationModal';
import Loadable from '@components/Loadable/Loadable';
import useToggle from '@hooks/useToggle';
import { IAdminGlobalMessage } from '@interfaces/adminGlobalMessages';
import {
  handleAdminDeleteGlobalMessage,
  handleAdminSetGlobalMessage,
  handleGetAdminGlobalMessage,
} from '@store/features/adminGlobalMessages/actions';
import {
  adminGlobalMessageErrorSelector,
  adminGlobalMessageIsDeletingSelector,
  adminGlobalMessageIsLoadingSelector,
  adminGlobalMessageSelector,
} from '@store/features/adminGlobalMessages/selectors';
import { removeAdminGlobalMessages } from '@store/features/adminGlobalMessages/slice';
import useDispatch from '@store/hooks/useDispatch';
import { encodeHtmlText } from '@views/AdminInquiriesPage/AdminInquiryList/SupplementInquiryDetails/SupplementInformationForm/utils';
import { EventType, useEventBus } from '@utils/eventBus';
import handleToast from '@utils/handleToast';

import GlobalMessageForm from './GlobalMessageForm/GlobalMessageForm';
import { validateForm } from './utils';

import './style.scss';

const GlobalMessages: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const eventBus = useEventBus('adminGlobalMessage');

  const data = useSelector(adminGlobalMessageSelector);
  const isLoading = useSelector(adminGlobalMessageIsLoadingSelector);
  const error = useSelector(adminGlobalMessageErrorSelector);
  const isDeleting = useSelector(adminGlobalMessageIsDeletingSelector);

  const [isEditing, handleEdit, handleCancel] = useToggle();
  const [showDeleteConfirmation, openDeleteConfirmation, closeDeleteConfirmation] = useToggle();

  const handleFetchGlobalMessage = useCallback(() => {
    dispatch(handleGetAdminGlobalMessage());
  }, [dispatch]);

  useEffect(() => {
    handleFetchGlobalMessage();
  }, [handleFetchGlobalMessage]);

  const handleSubmit = useCallback(
    (values: IAdminGlobalMessage) =>
      dispatch(
        handleAdminSetGlobalMessage({
          ...values,
          message: encodeHtmlText(values.message),
        }),
      ),
    [dispatch],
  );

  const handleDelete = useCallback(() => {
    if (data?.id) dispatch(handleAdminDeleteGlobalMessage(data.id));
  }, [data, dispatch]);

  useEffect(() => {
    eventBus.on(EventType.ADMIN_SET_GLOBAL_MESSAGE_SUCCESS, () => {
      handleToast('success', t('admin.setGlobalMessageSuccess'));
      handleFetchGlobalMessage();
      handleCancel();
    });

    eventBus.on(EventType.ADMIN_DELETE_GLOBAL_MESSAGE_SUCCESS, () => {
      handleToast('success', t('admin.deleteGlobalMessageSuccess'));
      closeDeleteConfirmation();
    });

    return () =>
      eventBus.off([
        EventType.ADMIN_SET_GLOBAL_MESSAGE_SUCCESS,
        EventType.ADMIN_DELETE_GLOBAL_MESSAGE_SUCCESS,
      ]);
  }, [t, eventBus, handleFetchGlobalMessage, handleCancel, closeDeleteConfirmation]);

  useEffect(
    () => () => {
      dispatch(removeAdminGlobalMessages());
    },
    [dispatch],
  );

  return (
    <>
      <div className="global-messages">
        <div className="global-messages__title">{t('admin.globalMessages')}</div>

        <div className="global-messages__body">
          <Loadable
            isLoading={isLoading}
            error={error && t('errors.globalMessage')}
            onRetry={handleFetchGlobalMessage}
          >
            <Form
              // @ts-ignore because of custom props
              component={GlobalMessageForm}
              initialValues={{
                ...data,
                message: data?.message ? (parse(data?.message) as string) : '',
              }}
              validate={validateForm}
              isNew={!data}
              isEditing={isEditing}
              onEdit={handleEdit}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              onDelete={openDeleteConfirmation}
            />
          </Loadable>
        </div>
      </div>

      <ConfirmationModal
        open={showDeleteConfirmation}
        isLoading={isDeleting}
        title={t('admin.deleteGlobalMessageConfirmation')}
        rightActionTitle={t('admin.delete')}
        onConfirm={handleDelete}
        onClose={closeDeleteConfirmation}
      />
    </>
  );
};

export default GlobalMessages;
